// import React from 'react';
// import { Box, Typography, useTheme, Paper } from '@mui/material';
// import { FaPills } from 'react-icons/fa';
// import { SectionProps, CarePlanFieldPath } from '../../../../types/ndisSection/carePlanInfoTypes';
// import { CareTextField, SectionHeader } from '../Components';

// const getNestedValue = (obj: any, path: string): string => {
//   if (!obj) return 'Not provided';
//   const keys = path.split('.');
//   let current = obj;
//   for (const key of keys) {
//     if (current && typeof current === 'object' && key in current) {
//       current = current[key];
//     } else {
//       return 'Not provided';
//     }
//   }
//   return current || 'Not provided';
// };

// const MedicationSection: React.FC<SectionProps> = ({
//   editMode,
//   isMobile,
//   handleEditToggle,
//   handleSubmit,
//   handleCancel,
//   control,
//   errors,
//   getValues,
//   carePlan
// }) => {
//   const theme = useTheme();
//   const spacing = isMobile ? 1 : 2;

//   return (
//     <Paper
//       elevation={2}
//       sx={{
//         backgroundColor: theme.palette.background.paper,
//         borderRadius: 2,
//         overflow: 'hidden',
//         width: '100%',
//         maxWidth: '100%',
//         mx: 'auto'
//       }}
//     >
//       <SectionHeader
//         icon={<FaPills size={isMobile ? 16 : 20} />}
//         title="Medication"
//         color={theme.palette.info.dark}
//         editMode={editMode}
//         handleEditToggle={handleEditToggle}
//         handleSubmit={handleSubmit}
//         handleCancel={handleCancel}
//         getValues={getValues}
//       />

//       <Box sx={{ p: isMobile ? 1 : 2 }}>
//         {editMode ? (
//           <CareTextField
//             label="Medication Details"
//             name="Medication"
//             control={control}
//             errors={errors}
//             getValues={getValues}
//             icon={<FaPills size={isMobile ? 14 : 16} />}
//             size={isMobile ? 'small' : 'medium'}
//             multiline
//             minRows={4}
//             placeholder="Enter medication details including name, dosage, frequency, and special instructions"
//           />
//         ) : (
//           <Paper
//             elevation={0}
//             sx={{
//               p: isMobile ? 1.5 : 2,
//               backgroundColor: theme.palette.background.default,
//               borderRadius: 1,
//               minHeight: isMobile ? 80 : 100,
//               display: 'flex',
//               alignItems: 'flex-start',
//               gap: spacing,
//               transition: 'all 0.2s ease',
//               '&:hover': {
//                 backgroundColor: theme.palette.action.hover
//               }
//             }}
//           >
//             <Box
//               sx={{
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 backgroundColor: theme.palette.info.light,
//                 color: theme.palette.info.contrastText,
//                 borderRadius: '50%',
//                 width: isMobile ? 36 : 42,
//                 height: isMobile ? 36 : 42,
//                 flexShrink: 0,
//                 mt: 0.5
//               }}
//             >
//               <FaPills size={isMobile ? 16 : 18} />
//             </Box>
//             <Box sx={{ flex: 1 }}>
//               <Typography
//                 variant={isMobile ? 'subtitle2' : 'h6'}
//                 sx={{
//                   fontWeight: 500,
//                   color: theme.palette.text.secondary,
//                   mb: 1
//                 }}
//               >
//                 Medication Details
//               </Typography>
//               <Typography
//                 variant="body1"
//                 sx={{
//                   whiteSpace: 'pre-wrap',
//                   wordBreak: 'break-word',
//                   color: theme.palette.text.primary
//                 }}
//               >
//                 {getNestedValue(carePlan, 'Medication') || 'No medication information provided'}
//               </Typography>
//             </Box>
//           </Paper>
//         )}
//       </Box>
//     </Paper>
//   );
// };

// export default MedicationSection;


import { Box, Typography, useTheme, Paper, Grid, IconButton, Button } from '@mui/material';
import React from 'react';
import { FaPills, FaTrash, FaPlus } from 'react-icons/fa';
import { useFieldArray } from 'react-hook-form';
import {
  SectionProps,
  CarePlanFieldPath,
  Medication
} from '../../../../types/ndisSection/carePlanInfoTypes';
import { CareTextField, SectionHeader } from '../Components';

const MedicationSection: React.FC<SectionProps> = ({
  editMode,
  isMobile,
  handleEditToggle,
  handleSubmit,
  handleCancel,
  control,
  errors,
  getValues
}) => {
  const theme = useTheme();
  const spacing = isMobile ? 1 : 2;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'Medication'
  });

  const defaultMedication: Medication = {
    Details: ''
  };

  // Normalize Medication to always be an array
  const normalizeMedication = (
    medication: Medication | Medication[] | string | undefined
  ): Medication[] => {
    if (!medication) {
      return [defaultMedication];
    }
    if (typeof medication === 'string') {
      return [{ Details: medication }];
    }
    if (!Array.isArray(medication)) {
      return [medication];
    }
    return medication.length > 0 ? medication : [defaultMedication];
  };

  return (
    <Paper
      elevation={2}
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        mx: 'auto'
      }}
    >
      <SectionHeader
        icon={<FaPills size={isMobile ? 16 : 20} />}
        title="Medication"
        color={theme.palette.info.dark}
        editMode={editMode}
        handleEditToggle={handleEditToggle}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        getValues={getValues}
      />

      <Box sx={{ p: isMobile ? 1 : 2 }}>
        {editMode ? (
          <>
            {fields.map((item, index) => (
              <Grid container spacing={spacing} key={item.id} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <CareTextField
                    label={`Medication Details ${index + 1}`}
                    name={`Medication[${index}].Details` as CarePlanFieldPath}
                    control={control}
                    errors={errors}
                    getValues={getValues}
                    icon={<FaPills size={isMobile ? 14 : 16} />}
                    size={isMobile ? 'small' : 'medium'}
                    multiline
                    minRows={4}
                    placeholder="Enter medication details including name, dosage, frequency, and special instructions"
                  />
                </Grid>
                <Grid item xs={12} textAlign="right">
                  <IconButton onClick={() => remove(index)} color="error">
                    <FaTrash />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              startIcon={<FaPlus />}
              variant="outlined"
              onClick={() => append(defaultMedication)}
              sx={{ mt: 1 }}
            >
              Add Medication
            </Button>
          </>
        ) : (
          <>
            {normalizeMedication(getValues('Medication')).map((item, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: isMobile ? 1.5 : 2,
                  backgroundColor: theme.palette.background.default,
                  borderRadius: 1,
                  minHeight: isMobile ? 80 : 100,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: spacing,
                  mb: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.info.light,
                    color: theme.palette.info.contrastText,
                    borderRadius: '50%',
                    width: isMobile ? 36 : 42,
                    height: isMobile ? 36 : 42,
                    flexShrink: 0,
                    mt: 0.5
                  }}
                >
                  <FaPills size={isMobile ? 16 : 18} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant={isMobile ? 'subtitle2' : 'h6'}
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.secondary,
                      mb: 1
                    }}
                  >
                    Medication Details {index + 1}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: theme.palette.text.primary
                    }}
                  >
                    {item.Details || 'No medication information provided'}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default MedicationSection;